export const MAX_APPETITE_SCALE = 15;

const LIKELIHOOD_WORDS = { 1: 'Rare', 2: 'Unlikely', 3: 'Possible', 4: 'Likely', 5: 'Almost certain' };
const IMPACT_WORDS = { 1: 'Insignificant', 2: 'Minor', 3: 'Moderate', 4: 'Major', 5: 'Severe' };
const STATUS_CLASS_MAP = { Open: 'bg-warning-subtle text-warning-emphasis', 'In progress': 'bg-warning-subtle text-warning-emphasis', Monitoring: 'bg-warning-subtle text-warning-emphasis', 'On track': 'bg-success-subtle text-success-emphasis', Escalated: 'bg-danger-subtle text-danger-emphasis', Mitigated: 'bg-success-subtle text-success-emphasis', Closed: 'bg-light text-secondary border border-light', Accepted: 'bg-light text-secondary border border-light', Overdue: 'bg-danger-subtle text-danger-emphasis' };
const DRAWER_STATUS_CLASS_MAP = { Open: 'risks-drawer-pill is-open', 'In progress': 'risks-drawer-pill is-warning', Monitoring: 'risks-drawer-pill is-warning', 'On track': 'risks-drawer-pill is-success', Escalated: 'risks-drawer-pill is-danger', Mitigated: 'risks-drawer-pill is-success', Closed: 'risks-drawer-pill is-neutral', Accepted: 'risks-drawer-pill is-neutral', Overdue: 'risks-drawer-pill is-danger' };
const DEPARTMENT_LABELS = { Finance: 'Finance', Procurement: 'Procurement', Compliance: 'Legal', 'IT security': 'IT Security', Operational: 'IT', People: 'HR', 'Data privacy': 'Legal', Strategic: 'Finance', Reputational: 'Finance', Financial: 'Finance' };
const CATEGORY_TO_RECORD_TYPE = { Procurement: 'Procurement', 'Data privacy': 'Data privacy', 'IT security': 'IT security', Compliance: 'Finance', Financial: 'Finance', Operational: 'IT security', People: 'Audit', Strategic: 'Report', Reputational: 'Report' };
const TREND_PATHS = { gradual: 'M10 18 C28 18 40 20 58 24 C66 26 72 28 82 32', shallow: 'M10 18 C30 18 44 19 60 22 C68 23 74 25 82 27', flat: 'M10 22 C30 22 48 22 64 23 C72 23 76 24 82 24' };

export const RECORD_ICON_MAP = { Controls: 'bi-shield-check', 'Corrective actions': 'bi-list-check', Incident: 'bi-bug', Supplier: 'bi-truck', Finance: 'bi-wallet2', Procurement: 'bi-cart', Audit: 'bi-clipboard-check', Report: 'bi-file-earmark-text', Contract: 'bi-bezier2', 'Data privacy': 'bi-shield-lock', 'IT security': 'bi-lock', Asset: 'bi-folder2-open' };
export function cloneDeep(value) {
  const cloned_value = JSON.parse(JSON.stringify(value));
  return cloned_value;
}
export function toneClassForStatus(status) {
  const tone_class = Reflect.get(DRAWER_STATUS_CLASS_MAP, status) || 'risks-drawer-pill is-neutral';
  return tone_class;
}
export function tableClassForStatus(status) {
  const table_class = Reflect.get(STATUS_CLASS_MAP, status) || 'bg-light text-secondary border border-light';
  return table_class;
}
export function actionClassForStatus(status) {
  const action_class = Reflect.get(DRAWER_STATUS_CLASS_MAP, status) || 'risks-drawer-pill is-neutral';
  return action_class;
}
export function appetite_status(peak, appetite) {
  const appetite_status_value = peak > appetite
    ? { label: 'Above appetite', class: 'bg-danger-subtle text-danger-emphasis' }
    : { label: 'Within appetite', class: 'bg-success-subtle text-success-emphasis' };
  return appetite_status_value;
}
export function appetite_width(peak) {
  const width = Math.max(8, Math.min(100, (peak / MAX_APPETITE_SCALE) * 100));
  return width;
}
export function appetite_marker_position(appetite) {
  const marker_position = Math.max(0, Math.min(100, (appetite / MAX_APPETITE_SCALE) * 100));
  return marker_position;
}
export function enrich_appetite_row(row) {
  const enriched_row = { ...row, width: appetite_width(row.peak), marker_position: appetite_marker_position(row.appetite), status: appetite_status(row.peak, row.appetite) };
  return enriched_row;
}
export function createDefaultRiskForm() {
  const default_risk_form = { risk: '', category: 'Strategic', department: '', owner: '', review: '', status: 'Open', inherent_likelihood: 3, inherent_impact: 3, residual_likelihood: 2, residual_impact: 2 };
  return default_risk_form;
}
export function buildStatCard(label, value, note, tone) {
  const stat_card = { label, value, note, tone };
  return stat_card;
}
export function buildTrendCard(path_key = 'gradual') {
  const trend_path = Reflect.get(TREND_PATHS, path_key) || TREND_PATHS.gradual;
  const trend_card = { label: '6-month trend', note: 'Reducing', path: trend_path };
  return trend_card;
}
function likelihoodWord(value) {
  const likelihood_word = Reflect.get(LIKELIHOOD_WORDS, value) || 'Possible';
  return likelihood_word;
}
function impactWord(value) {
  const impact_word = Reflect.get(IMPACT_WORDS, value) || 'Moderate';
  return impact_word;
}
function noteFromScores(likelihood, impact) {
  const score_note = `${likelihoodWord(likelihood)} x ${impactWord(impact)}`;
  return score_note;
}

function inherentToneFromScore(score) {
  const tone = score >= 15 ? 'danger' : score >= 8 ? 'warning' : 'neutral';
  return tone;
}

function residualToneFromScore(score) {
  const tone = score >= 8 ? 'warning' : 'neutral';
  return tone;
}
function departmentFromCategory(category, fallback = '') {
  const department_label = Reflect.get(DEPARTMENT_LABELS, category) || fallback || category || 'Finance';
  return department_label;
}
function categoryRecordType(category) {
  const record_type = Reflect.get(CATEGORY_TO_RECORD_TYPE, category) || 'Report';
  return record_type;
}
function buildGenericConnectedRecords(row, department) {
  const connected_records = [
    { type: 'Controls', title: row.residual < row.inherent ? '1 linked' : 'No linked controls', tone: row.residual < row.inherent ? 'green' : 'slate' },
    { type: categoryRecordType(row.category), title: `${department} register`, tone: 'teal' },
    { type: 'Report', title: 'Board risk report', tone: 'teal' },
    { type: 'Audit', title: `${department} review`, tone: 'teal' }
  ];
  return connected_records;
}
function buildGenericControls(row) {
  if (row.residual >= row.inherent) return [];
  const controls = [{ title: `C-${100 + Number.parseInt(String(row.ref).replace(/\D/g, ''), 10)} - ${row.category} review control in place`, subtitle: 'Mitigating control' }];
  return controls;
}
function buildGenericMitigations(row, department) {
  const mitigation_status = row.status.label === 'Escalated' ? 'In progress' : row.status.label;
  const mitigations = [{ title: `Progress mitigation for ${row.risk}`, subtitle: `${department} - due Next review`, status: mitigation_status }];
  return mitigations;
}

export function buildGenericRiskDrawer(row, form) {
  const department = departmentFromCategory(row.category, form.department);
  const review = form.review || 'Quarterly - next Jul 2026';
  return {
    section: `Risk - ${row.ref} - ${row.category || 'Strategic'}`,
    status: row.status.label,
    title: row.risk,
    subtitle: `Owner ${row.owner} - ${department} - review ${review}`,
    can_escalate: row.status.label !== 'Escalated',
    stats: { inherent: buildStatCard('Inherent', row.inherent, noteFromScores(form.inherent_likelihood, form.inherent_impact), inherentToneFromScore(row.inherent)), residual: buildStatCard('Residual', row.residual, noteFromScores(form.residual_likelihood, form.residual_impact), residualToneFromScore(row.residual)), trend: buildTrendCard(row.residual < row.inherent ? 'shallow' : 'flat') },
    connected_records: buildGenericConnectedRecords(row, department),
    controls: buildGenericControls(row),
    mitigation_actions: buildGenericMitigations(row, department)
  };
}
